'use client';

import React, { Component, ErrorInfo, ReactNode } from 'react';
import { logError } from '../lib/errorLogger';

interface Props {
  children: ReactNode;
  /** Optional label shown in the fallback UI to identify which section crashed. */
  label?: string;
  /** Optional callback invoked when the user clicks "Reload". */
  onReset?: () => void;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

/**
 * Wraps a subtree and renders a fallback UI if any descendant throws during
 * render. Prevents a single broken widget from crashing the whole page.
 *
 * Usage:
 *   <ErrorBoundary label="Dashboard widgets">
 *     <HomeSalesDashboard />
 *   </ErrorBoundary>
 */
export class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false, error: null };

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('[ErrorBoundary]', this.props.label ?? 'section', error, info.componentStack);
    logError(error, {
      stack: `${error.stack ?? ''}\n--- component stack (${this.props.label ?? 'section'}) ---${info.componentStack ?? ''}`,
    });
  }

  handleReload = () => {
    this.setState({ hasError: false, error: null });
    this.props.onReset?.();
  };

  render() {
    if (!this.state.hasError) return this.props.children;
    const { label } = this.props;
    return (
      <div className="m-2 rounded-rustic border border-rust/30 bg-rust/5 p-4 text-sm">
        <div className="flex items-start gap-3">
          <svg className="mt-0.5 h-5 w-5 flex-shrink-0 text-rust-dark" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
            <path fillRule="evenodd" d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495zM10 5a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 5zm0 9a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
          </svg>
          <div className="flex-1">
            <p className="font-medium text-rust-dark">
              {label ? `Error in section: ${label}` : 'An error occurred in this section.'}
            </p>
            <p className="mt-1 text-xs text-wood">
              {this.state.error?.message ?? 'Unknown rendering error.'}
            </p>
            <div className="mt-3 flex gap-2">
              <button
                type="button"
                onClick={this.handleReload}
                className="rounded-rustic border border-wheat bg-cream px-3 py-1.5 text-xs font-medium text-bark hover:bg-wheat/30 transition"
              >
                Try again
              </button>
              <button
                type="button"
                onClick={() => window.location.reload()}
                className="rounded-rustic border border-wheat bg-cream px-3 py-1.5 text-xs font-medium text-bark hover:bg-wheat/30 transition"
              >
                Reload page
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }
}

export default ErrorBoundary;
